import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Code,
  Group,
  Modal,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
  Tooltip,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import {
  IconAlertCircle,
  IconCheck,
  IconCirclePlus,
  IconCopy,
  IconKey,
  IconX,
} from "@tabler/icons";
import moment from "moment";

import { Page } from "../../layout/page";
import { useServerApi } from "../../hooks/userServerApi";

const apiEntity = "apiKey";
const sessionPlainKeyPrefix = "mbid_apiKey_plain_";

const getSessionPlainKey = (id) => {
  if (!id) return null;
  try {
    return sessionStorage.getItem(`${sessionPlainKeyPrefix}${id}`);
  } catch {
    return null;
  }
};

const setSessionPlainKey = (id, plainKey) => {
  if (!id || !plainKey) return;
  try {
    sessionStorage.setItem(`${sessionPlainKeyPrefix}${id}`, plainKey);
  } catch {
    // ignore quota / private mode errors
  }
};

const formatDate = (value) => {
  if (!value) return "-";
  return moment(value).format("YYYY-MM-DD HH:mm");
};

const StatusBadge = ({ status }) => {
  const color = status === "ACTIVE" ? "green" : "gray";
  return <Badge color={color}>{status}</Badge>;
};

const ApiKeyPage = () => {
  const [api] = useServerApi();
  const keyInputRef = useRef(null);

  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [revealed, setRevealed] = useState(null);

  const [confirmRevoke, setConfirmRevoke] = useState(null);

  const loadKeys = async () => {
    setLoading(true);
    try {
      const result = await api.search({
        apiEntity,
        pageSize: 200,
        currentPage: 1,
        searchQuery: {},
        sort: { by: "createdAt", order: "desc" },
        byAggregation: false,
      });
      setKeys(result?.docs ?? []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadKeys();
  }, []);

  const handleCreate = async () => {
    if (!name.trim()) {
      showNotification({
        title: "Name is required",
        color: "red",
        icon: <IconX size={18} />,
        message: "Please give the key a name to identify it later.",
      });
      return;
    }
    setSubmitting(true);
    try {
      const result = await api.ApiKey.create({
        name: name.trim(),
        description: description.trim(),
      });
      if (!result?.success) {
        showNotification({
          title: "Failed to create API key",
          color: "red",
          icon: <IconX size={18} />,
          message: result?.message ?? "Unknown error",
        });
        return;
      }
      setCreateOpen(false);
      setName("");
      setDescription("");
      if (result.data?.plainKey && result.data?._id) {
        setSessionPlainKey(result.data._id, result.data.plainKey);
      }
      setRevealed(result.data);
      loadKeys();
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (key) => {
    const result = await api.ApiKey.revoke(key._id);
    if (!result?.success) {
      showNotification({
        title: "Failed to revoke",
        color: "red",
        icon: <IconX size={18} />,
        message: result?.message ?? "Unknown error",
      });
      return;
    }
    showNotification({
      title: "API key revoked",
      color: "green",
      icon: <IconCheck size={18} />,
      message: `${key.name} can no longer be used.`,
    });
    setConfirmRevoke(null);
    loadKeys();
  };

  useEffect(() => {
    if (!revealed?.plainKey) return;
    const timer = window.setTimeout(() => {
      keyInputRef.current?.focus();
      keyInputRef.current?.select();
    }, 100);
    return () => window.clearTimeout(timer);
  }, [revealed]);

  const copyPlaintext = async (text) => {
    if (!text) {
      showNotification({
        title: "Nothing to copy",
        color: "red",
        icon: <IconX size={18} />,
        message: "The full API key is not available.",
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
    } catch {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      } catch (error) {
        showNotification({
          title: "Copy failed",
          color: "red",
          icon: <IconX size={18} />,
          message: "Select the key and copy it manually (Cmd/Ctrl+C).",
        });
        return;
      }
    }

    showNotification({
      title: "Copied",
      color: "green",
      icon: <IconCheck size={18} />,
      message: "API key copied to clipboard.",
    });
  };

  return (
    <Page
      title={"API Keys"}
      actions={
        <Button
          leftIcon={<IconCirclePlus size={16} />}
          onClick={() => setCreateOpen(true)}
        >
          Create API Key
        </Button>
      }
    >
      <Text size="sm" color="dimmed" mb="md">
        API keys let external integrations call MBID without going through the
        normal login flow. Treat them like passwords. The full key is only
        shown once at creation; if it is lost, revoke it and create a new one.
      </Text>

      <Table striped withBorder verticalSpacing="sm">
        <thead>
          <tr>
            <th>Name</th>
            <th>Prefix</th>
            <th>Status</th>
            <th>Created</th>
            <th>Last Used</th>
            <th style={{ width: 140, textAlign: "right" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {keys.length === 0 && !loading && (
            <tr>
              <td colSpan={6}>
                <Text align="center" color="dimmed" py="md">
                  No API keys yet. Click "Create API Key" to add one.
                </Text>
              </td>
            </tr>
          )}
          {keys.map((key) => (
            <tr key={key._id}>
              <td>
                <Group spacing="xs" noWrap>
                  <IconKey size={16} />
                  <span>
                    <Text size="sm" weight={500}>
                      {key.name}
                    </Text>
                    {key.description ? (
                      <Text size="xs" color="dimmed" lineClamp={1}>
                        {key.description}
                      </Text>
                    ) : null}
                  </span>
                </Group>
              </td>
              <td>
                <Code>{key.keyPrefix}…</Code>
              </td>
              <td>
                <StatusBadge status={key.status} />
              </td>
              <td>
                <Text size="xs">{formatDate(key.createdAt)}</Text>
                {key.createdBy?.name ? (
                  <Text size="xs" color="dimmed">
                    by {key.createdBy.name}
                  </Text>
                ) : null}
              </td>
              <td>
                <Text size="xs">{formatDate(key.lastUsedAt)}</Text>
                {key.lastUsedIp ? (
                  <Text size="xs" color="dimmed">
                    {key.lastUsedIp}
                  </Text>
                ) : null}
              </td>
              <td style={{ textAlign: "right" }}>
                {key.status === "ACTIVE" ? (
                  <Group spacing="xs" position="right" noWrap>
                    {getSessionPlainKey(key._id) ? (
                      <Button
                        size="xs"
                        variant="light"
                        leftIcon={<IconCopy size={14} />}
                        onClick={() =>
                          copyPlaintext(getSessionPlainKey(key._id))
                        }
                      >
                        Copy
                      </Button>
                    ) : null}
                    <Button
                      size="xs"
                      color="red"
                      variant="light"
                      onClick={() => setConfirmRevoke(key)}
                    >
                      Revoke
                    </Button>
                  </Group>
                ) : (
                  <Text size="xs" color="dimmed">
                    Revoked {formatDate(key.revokedAt)}
                  </Text>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Modal
        opened={createOpen}
        onClose={() => {
          if (submitting) return;
          setCreateOpen(false);
          setName("");
          setDescription("");
        }}
        title={<Title order={4}>Create API Key</Title>}
      >
        <Stack>
          <TextInput
            label="Name"
            placeholder="e.g. Webhook integration"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            required
            data-autofocus
          />
          <Textarea
            label="Description"
            placeholder="Optional. What is this key used for?"
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
            minRows={2}
          />
          <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
            The full key will only be shown once after creation. Make sure to
            copy and store it somewhere safe.
          </Alert>
          <Group position="right">
            <Button
              variant="default"
              onClick={() => setCreateOpen(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button onClick={handleCreate} loading={submitting}>
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={Boolean(revealed)}
        onClose={() => setRevealed(null)}
        title={<Title order={4}>Your new API key</Title>}
        closeOnClickOutside={false}
        closeOnEscape={false}
        withCloseButton={false}
      >
        <Stack>
          <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
            Copy this key now. For security, MBID stores only a hashed copy and
            cannot show it again after you close this browser tab.
          </Alert>
          <Text size="sm" weight={500}>
            {revealed?.name}
          </Text>
          <Group spacing="xs" noWrap align="flex-end">
            <TextInput
              ref={keyInputRef}
              readOnly
              label="API key"
              value={revealed?.plainKey ?? ""}
              onFocus={(event) => event.currentTarget.select()}
              onClick={(event) => event.currentTarget.select()}
              styles={{ input: { fontFamily: "monospace" } }}
              style={{ flex: 1 }}
            />
            <Tooltip label="Copy to clipboard">
              <Button
                variant="light"
                leftIcon={<IconCopy size={16} />}
                onClick={() => copyPlaintext(revealed?.plainKey)}
              >
                Copy
              </Button>
            </Tooltip>
          </Group>
          <Text size="xs" color="dimmed">
            Send this key in requests using the <code>x-api-key</code> header
            or <code>Authorization: Bearer &lt;key&gt;</code>.
          </Text>
          <Group position="right">
            <Button onClick={() => setRevealed(null)}>I have saved it</Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={Boolean(confirmRevoke)}
        onClose={() => setConfirmRevoke(null)}
        title={<Title order={4}>Revoke API key?</Title>}
      >
        <Stack>
          <Text size="sm">
            Once revoked, <b>{confirmRevoke?.name}</b> can no longer
            authenticate. This cannot be undone.
          </Text>
          <Group position="right">
            <Button variant="default" onClick={() => setConfirmRevoke(null)}>
              Cancel
            </Button>
            <Button color="red" onClick={() => handleRevoke(confirmRevoke)}>
              Revoke
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Page>
  );
};

export default ApiKeyPage;
